import { useNavigate } from "@tanstack/react-router";
import type { Metric, Report } from "@unbox/api";
import {
  CursorClick01,
  Download01,
  FilterLines,
  LineChartUp01,
  Plus,
  PresentationChart01,
  SearchMd,
  Settings03,
  X,
  ZoomIn,
} from "@untitledui/icons";
import type React from "react";
import { useCallback, useState } from "react";
import { usePagination } from "@/api/pagination";
import {
  type CreateReportInput,
  type UpdateReportInput,
  useCreateReport,
  useDeleteReport,
  useReports,
  useUpdateReport,
} from "@/api/reports";
import { useDismissHero } from "@/api/user-preferences";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { PageHero, PageHeroKey } from "@/components/application/page-hero/page-hero";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { FilterPill } from "@/components/filters/filter-pills";
import { ReportFilterPanel } from "@/components/filters/panels/report-filter-panel";
import { GenericLanding } from "@/components/GenericLanding";
import { LoadingPageState } from "@/components/loading-page-state";
import { PageLayout } from "@/components/page-layout";
import { METRICS_MAP } from "@/constants/metrics";
import type { ReportFilterId, ReportFiltersState } from "@/hooks/use-report-filters";
import {
  applyReportOperator,
  clearReportFilter,
  getDefaultReportFilters,
  setReportOperator,
} from "@/hooks/use-report-filters";
import { useSort } from "@/hooks/use-sort";
import { useToast } from "@/hooks/use-toast";
import { cx } from "@/utils/cx";
import { ReportConfigurationModal } from "./components/ReportConfigurationModal";
import { ReportsEmptyState } from "./reports-empty-state";
import { ReportsTable } from "./reports-table";

export type TabType = "all" | "charts" | "tables";
export type ReportSort = "name" | "createdAt" | "updatedAt" | "reportLayout" | "reportMetrics";

// Format reports data for display
const formatReportsForDisplay = (
  reports: Array<
    Omit<Report, "createdAt" | "updatedAt" | "startDate" | "endDate"> & {
      createdAt: string;
      updatedAt: string;
      startDate: string | null;
      endDate: string | null;
    }
  >,
) => {
  return reports.map((report) => ({
    id: report.id,
    name: report.name,
    layout: report.layout,
    metrics: Array.isArray(report.metrics) ? report.metrics.map((m) => METRICS_MAP[m].label).join(", ") : "No metrics",
    createdAt: report.createdAt ? new Date(report.createdAt) : null,
    updatedAt: report.updatedAt ? new Date(report.updatedAt) : null,
    templateId: report.templateId,
  }));
};

export type ReportRow = ReturnType<typeof formatReportsForDisplay>[number];

// Filter Pills Component for Reports
interface ReportFilterPillsProps {
  filters: ReportFiltersState;
  onFiltersChange: (filters: ReportFiltersState) => void;
}

const formatPillDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

const ReportFilterPills: React.FC<ReportFilterPillsProps> = ({ filters, onFiltersChange }) => {
  const pills: Array<{
    id: ReportFilterId;
    label: string;
    value: string;
    cleared: Partial<ReportFiltersState>;
    canNegate: boolean;
  }> = [];

  if (filters.layout !== "all") {
    pills.push({
      id: "layout",
      label: "Layout",
      value: filters.layout === "chart" ? "Chart" : "Table",
      cleared: { layout: "all" },
      canNegate: false,
    });
  }

  if (filters.metrics.length > 0) {
    const metricsLabels = filters.metrics.map((m) => METRICS_MAP[m]?.label ?? m);
    const displayValue =
      metricsLabels.length === 1 ? metricsLabels[0] : `${metricsLabels[0]} +${metricsLabels.length - 1}`;
    pills.push({ id: "metrics", label: "Metrics", value: displayValue, cleared: { metrics: [] }, canNegate: true });
  }

  if (filters.createdDateRange) {
    pills.push({
      id: "createdDateRange",
      label: "Created",
      value: `${formatPillDate(filters.createdDateRange.start)} - ${formatPillDate(filters.createdDateRange.end)}`,
      cleared: { createdDateRange: null },
      canNegate: true,
    });
  }

  if (filters.editedDateRange) {
    pills.push({
      id: "editedDateRange",
      label: "Last Edited",
      value: `${formatPillDate(filters.editedDateRange.start)} - ${formatPillDate(filters.editedDateRange.end)}`,
      cleared: { editedDateRange: null },
      canNegate: true,
    });
  }

  if (pills.length === 0) {
    return null;
  }

  const clearAll = () => {
    onFiltersChange(getDefaultReportFilters());
  };

  return (
    <div className="py-2 -my-2">
      <div className="flex items-start gap-2">
        <span className="text-xs text-tertiary font-medium whitespace-nowrap flex-shrink-0 pt-1.5">Filters:</span>
        <div className="flex-1 flex flex-wrap gap-1.5">
          {pills.map((pill) => (
            <FilterPill
              key={pill.id}
              label={pill.label}
              value={pill.value}
              onRemove={() => onFiltersChange(clearReportFilter(filters, pill.id, pill.cleared))}
              operator={filters.negated.includes(pill.id) ? "is_not" : "is"}
              onOperatorChange={
                pill.canNegate
                  ? (operator) => onFiltersChange(setReportOperator(filters, pill.id, operator))
                  : undefined
              }
            />
          ))}
        </div>
        {pills.length > 1 && (
          <button
            type="button"
            onClick={clearAll}
            className="text-xs text-tertiary hover:text-primary font-medium whitespace-nowrap transition-colors pt-1"
          >
            Clear all
          </button>
        )}
      </div>
    </div>
  );
};

interface ReportsLandingPageProps {
  onCreateReport?: () => void;
}

export const ReportsLanding: React.FC<ReportsLandingPageProps> = ({ onCreateReport }) => {
  const reportsFeatures = [
    {
      icon: LineChartUp01,
      text: "Performance metrics that matter most to you",
    },
    {
      icon: CursorClick01,
      text: "Interactive charts that make trends clearer",
    },
    {
      icon: Download01,
      text: "Export capabilities for presentations and meetings",
    },
    {
      icon: FilterLines,
      text: "Filter and segment data to uncover hidden patterns",
    },
  ];

  return (
    <GenericLanding
      title="Reports"
      subtitle="Track what you care about most with custom reports"
      description="Build personalised views that reveal the insights driving your success with powerful analytics and visualisations."
      features={reportsFeatures}
      buttonText="Create new report"
      iconComponent={LineChartUp01}
      buttonIcon={Plus}
      visualAsset="/chart-container-new.svg"
      onCreateAction={onCreateReport}
    />
  );
};

export const Reports: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  // State
  const [search, setSearch] = useState("");
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingReport, setEditingReport] = useState<Report | null>(null);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [filters, setFilters] = useState<ReportFiltersState>(getDefaultReportFilters());
  const itemsPerPage = 10;
  const pagination = usePagination({ limit: itemsPerPage, offset: 0 });

  const { sort, setFromDescriptor, descriptor } = useSort<ReportSort>("createdAt");

  // Reports API hooks
  const { data: savedReports = [], isLoading: isLoadingReports } = useReports();
  const createReport = useCreateReport();
  const dismissHero = useDismissHero();
  const updateReport = useUpdateReport();
  const deleteReport = useDeleteReport();

  const reportsData = formatReportsForDisplay(savedReports);

  // Helper function to check if a date falls within the selected date range
  const isWithinDateRange = useCallback((date: Date | null, dateRange: { start: string; end: string } | null) => {
    if (!date || !dateRange) return true;

    // Set time to start/end of day for proper comparison
    const reportDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const fromDate = new Date(dateRange.start);
    const toDate = new Date(dateRange.end);

    return reportDate >= fromDate && reportDate <= toDate;
  }, []);

  // Filter reports based on layout, search, metrics, and date ranges
  const filteredReports = reportsData.filter((report) => {
    // Layout filter
    const matchesLayout = filters.layout === "all" || filters.layout === report.layout;

    // Search filter
    const matchesSearch = report.name.toLowerCase().includes(search.toLowerCase());

    // Metrics filter - check if report contains any of the selected metric filters
    const matchesMetrics = applyReportOperator(
      filters,
      "metrics",
      filters.metrics.length > 0,
      filters.metrics.length === 0 ||
        filters.metrics.some((metric) => {
          const metricLabel = METRICS_MAP[metric]?.label ?? metric;
          return report.metrics.toLowerCase().includes(metricLabel.toLowerCase());
        }),
    );

    // Created date range filter
    const matchesCreatedDateRange = applyReportOperator(
      filters,
      "createdDateRange",
      !!filters.createdDateRange,
      isWithinDateRange(report.createdAt, filters.createdDateRange),
    );

    // Edited date range filter
    const matchesEditedDateRange = applyReportOperator(
      filters,
      "editedDateRange",
      !!filters.editedDateRange,
      isWithinDateRange(report.updatedAt, filters.editedDateRange),
    );

    return matchesLayout && matchesSearch && matchesMetrics && matchesCreatedDateRange && matchesEditedDateRange;
  });

  // Sort filtered reports using useSort hook
  const sortedReports = [...filteredReports].sort((a, b) => {
    if (!sort.column) return 0;

    let aValue: string | Date | null;
    let bValue: string | Date | null;

    switch (sort.column) {
      case "name":
        aValue = a.name;
        bValue = b.name;
        break;
      case "createdAt":
        aValue = a.createdAt;
        bValue = b.createdAt;
        break;
      case "updatedAt":
        aValue = a.updatedAt;
        bValue = b.updatedAt;
        break;
      case "reportLayout":
        aValue = a.layout;
        bValue = b.layout;
        break;
      case "reportMetrics":
        aValue = a.metrics;
        bValue = b.metrics;
        break;
      default:
        return 0;
    }

    // Handle null/undefined values
    if (aValue === null && bValue === null) return 0;
    if (aValue === null) return 1;
    if (bValue === null) return -1;

    let comparison: number;
    if (aValue instanceof Date && bValue instanceof Date) {
      comparison = aValue.getTime() - bValue.getTime();
    } else {
      comparison = String(aValue).localeCompare(String(bValue));
    }

    return sort.direction === "asc" ? comparison : -comparison;
  });

  // Get counts for layout types
  const allCount = reportsData.length;
  const chartsCount = reportsData.filter((report) => report.layout === "chart").length;
  const tablesCount = reportsData.filter((report) => report.layout === "table").length;

  // Count of active filters for badge display
  const activeFiltersCount =
    (filters.layout !== "all" ? 1 : 0) +
    (filters.metrics.length > 0 ? 1 : 0) +
    (filters.createdDateRange ? 1 : 0) +
    (filters.editedDateRange ? 1 : 0);

  const paginatedReports = sortedReports.slice(
    pagination.pagination.offset,
    pagination.pagination.offset + pagination.pagination.limit,
  ); // TODO(LH): Bootleg pagination - will eventually need query pagination as well.

  // Chart save/load functions
  const handleSaveChart = async (data: {
    name: string;
    layout: "chart" | "table" | "bar";
    metrics: Metric[];
    templateId?: string | null;
    startDate?: string;
    endDate?: string;
  }) => {
    try {
      const reportInput: CreateReportInput = {
        name: data.name,
        layout: data.layout,
        metrics: data.metrics,
        templateId: data.templateId,
        startDate: data.startDate ?? null,
        endDate: data.endDate ?? null,
      };

      const newReportId = await createReport.mutateAsync(reportInput);

      toast({
        title: `${data.layout === "chart" ? "Chart" : "Table"} created`,
        description: `"${data.name}" successfully created.`,
      });

      setSaveDialogOpen(false);
      return newReportId;
    } catch {
      toast({ title: "Error", description: "Failed to save report. Please try again.", variant: "destructive" });
      return null;
    }
  };

  const handleEditReport = (reportId: string) => {
    const originalReport = savedReports.find((r) => r.id === reportId);
    if (originalReport) {
      // Convert string dates to Date objects for the Report type
      setEditingReport({
        ...originalReport,
        createdAt: new Date(originalReport.createdAt),
        updatedAt: new Date(originalReport.updatedAt),
        startDate: originalReport.startDate ? new Date(originalReport.startDate) : null,
        endDate: originalReport.endDate ? new Date(originalReport.endDate) : null,
      } as Report);
      setEditDialogOpen(true);
    }
  };

  const handleUpdateReport = async (data: { name: string; layout: "chart" | "table" | "bar"; metrics: Metric[] }) => {
    if (!editingReport) return null;

    try {
      const reportInput: UpdateReportInput = {
        name: data.name,
        layout: data.layout,
        metrics: data.metrics,
      };

      await updateReport.mutateAsync({ id: editingReport.id, input: reportInput });
      toast({
        title: `${data.layout === "chart" ? "Chart" : "Table"} updated`,
        description: `"${data.name}" successfully updated.`,
      });

      setEditDialogOpen(false);
      setEditingReport(null);
      return { id: editingReport.id };
    } catch {
      toast({ title: "Error", description: "Failed to update report. Please try again.", variant: "destructive" });
      return null;
    }
  };

  const handleDeleteReport = async (reportId: string, reportName: string, layout: "chart" | "table" | "bar") => {
    try {
      await deleteReport.mutateAsync(reportId);
      toast({
        title: `${layout === "chart" ? "Chart" : "Table"} deleted`,
        description: `"${reportName}" successfully deleted.`,
      });
    } catch {
      toast({ title: "Error", description: "Failed to delete report. Please try again.", variant: "destructive" });
    }
  };

  const handleNavigateToReport = (reportId: string) => {
    console.log("Navigating to report:", reportId);
    navigate({ to: `/reports/${reportId}` });
  };

  const resetFilters = () => {
    setSearch("");
    setFilters(getDefaultReportFilters());
    pagination.setPage(1);
  };

  // Check if there are any active filters
  const hasActiveFilters = search || activeFiltersCount > 0;

  if (isLoadingReports && reportsData.length === 0) return <LoadingPageState />;

  // If no reports exist at all and no filters are active, the full-bleed empty
  // state replaces the whole page body.
  if (reportsData.length === 0 && !hasActiveFilters && !isLoadingReports) {
    return (
      <>
        <ReportConfigurationModal
          isOpen={saveDialogOpen}
          onOpenChange={setSaveDialogOpen}
          onSave={async (data) => {
            const newReportId = await handleSaveChart(data);
            if (newReportId) dismissHero.mutate(PageHeroKey.Reports);
            return newReportId;
          }}
          isCreating={createReport.isPending}
        />
        <ReportsEmptyState onBuildReport={() => setSaveDialogOpen(true)} />
      </>
    );
  }

  return (
    <PageLayout
      drawerOpen={filterPanelOpen}
      title="Reports"
      icon={LineChartUp01}
      subtitle="Create custom views to monitor the statistics you care about"
      callToAction={
        <Button
          iconLeading={<Plus className="w-4 h-4 mr-2" />}
          onClick={() => setSaveDialogOpen(true)}
          className="flex items-center justify-between primary"
        >
          Create new report
        </Button>
      }
    >
      <PageHero
        pageKey={PageHeroKey.Reports}
        title="Build reports that matter to you."
        text1={{ icon: Settings03, message: "Pick the metrics you care about, like GMV and videos posted." }}
        text2={{ icon: PresentationChart01, message: "Show them as a chart or a table." }}
        text3={{ icon: ZoomIn, message: "Zoom in on any date range, from a single day to a full quarter." }}
        image="/heroes/reports.png"
      />
      {/* Filter Panel */}
      <ReportFilterPanel
        open={filterPanelOpen}
        close={() => setFilterPanelOpen(false)}
        filters={filters}
        onFiltersChange={setFilters}
      />

      <div className="flex flex-col gap-5 w-full flex-1 min-h-0">
        {/* Create Report Modal */}
        <ReportConfigurationModal
          isOpen={saveDialogOpen}
          onOpenChange={setSaveDialogOpen}
          onSave={handleSaveChart}
          isCreating={createReport.isPending}
        />

        {/* Edit Report Modal */}
        <ReportConfigurationModal
          isOpen={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          onSave={handleUpdateReport}
          isCreating={updateReport.isPending}
          editMode={true}
          existingReport={
            editingReport
              ? {
                  id: editingReport.id,
                  name: editingReport.name,
                  layout: editingReport.layout,
                  metrics: Array.isArray(editingReport.metrics) ? editingReport.metrics : [],
                }
              : undefined
          }
        />

        <div className="flex-1 flex flex-col min-h-0">
          {/* Navigation - Single Row with Count and Search/Filters */}
          <div className="flex flex-row items-center justify-between pb-6 rounded-xl gap-4">
            {/* Left side: Report Count */}
            <div className="flex items-center flex-row gap-4">
              <span className="text-sm font-semibold text-gray-500">
                {filters.layout === "all" && `${allCount} Report${allCount !== 1 ? "s" : ""}`}
                {filters.layout === "chart" && `${chartsCount} Chart${chartsCount !== 1 ? "s" : ""}`}
                {filters.layout === "table" && `${tablesCount} Table${tablesCount !== 1 ? "s" : ""}`}
              </span>
            </div>

            {/* Right side: Search and Filters Button */}
            <div className="flex items-center flex-row gap-2">
              <Input
                size="sm"
                inputClassName="text-sm font-semibold"
                className="w-96"
                value={search}
                onClear={() => setSearch("")}
                onChange={setSearch}
                placeholder="Search"
                icon={SearchMd}
              />

              <Button
                size="sm"
                color="secondary"
                className="font-medium text-xs"
                iconLeading={<FilterLines className="w-4 h-4" />}
                onClick={() => setFilterPanelOpen(true)}
              >
                Filter
              </Button>
            </div>
          </div>

          {/* Filter Pills */}
          <ReportFilterPills filters={filters} onFiltersChange={setFilters} />

          {/* Reports Table */}
          <ReportsTable
            rows={isLoadingReports ? undefined : paginatedReports}
            onClickRow={(row) => handleNavigateToReport(row.id)}
            sorting={{ descriptor, onSortChange: setFromDescriptor }}
            pagination={{
              page: pagination.page,
              totalPages: Math.ceil((sortedReports.length ?? 0) / pagination.pagination.limit),
              onPageChange: pagination.setPage,
            }}
            hasActiveFilters={!!hasActiveFilters}
            onClearFilters={resetFilters}
            onDeleteReport={handleDeleteReport}
            onEditReport={handleEditReport}
          />
        </div>
      </div>
    </PageLayout>
  );
};
